import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/pro-light-svg-icons/faMagnifyingGlass';
import { TypedMemo } from '../../common/types';
import { IntegrationTypeIcon } from '../../music24/components/IntegrationTypeIcon/IntegrationTypeIcon';
import { promotedServices } from './types';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the sheet; each part uses its own slice of it */
import styles from './SMLCreationSheet.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface CreationOmniFieldProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
}

/**
 * One field for both ways in.
 *
 * Pasting used to be a link at the bottom of a search step — "Paste A Link
 * Instead" — even though most artists arrive with a link in the clipboard.
 * One field takes either and tells them apart.
 */
const CreationOmniFieldComponent: React.FC<CreationOmniFieldProps> = ({ value, onChange, onSubmit }) => {
  const intl = useIntl();

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => onChange(event.target.value),
    [onChange]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        onSubmit();
      }
    },
    [onSubmit]
  );

  const label = intl.formatMessage({
    id: 'app.sml.create.omni.placeholder',
    defaultMessage: 'Paste a release link, or search your catalog',
  });

  return (
    <div className={styles.omni}>
      <div className={styles.omniRow}>
        <FontAwesomeIcon icon={faMagnifyingGlass} />
        <input
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          className={styles.omniInput}
          value={value}
          placeholder={label}
          aria-label={label}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
        />
      </div>
      <div className={styles.omniServices}>
        <span className={styles.omniMarks}>
          {promotedServices.map((source) => (
            <IntegrationTypeIcon key={source} integrationType={source} size={15} />
          ))}
        </span>
        <span>
          {intl.formatMessage({
            id: 'app.sml.create.omni.services',
            defaultMessage: 'Any of these works — we find the rest ourselves.',
          })}
        </span>
      </div>
    </div>
  );
};

export const CreationOmniField = TypedMemo(CreationOmniFieldComponent);
